import { useState, useEffect } from "react";
import { useParams } from "react-router";
import moment from "moment";
import "moment-timezone";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import ExternalLink from "../../components/utilities/ExternalLink";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { handlePagingAll } from "../../components/api/APIUtilities";
import {
  BCPAngleDown,
  BCPCircleArrowUp,
} from "../../components/icons/bcp/BCPIcons";
import { getData } from "../../components/utilities/UtilityFunctions";

export default function EventCalendar(props) {
  let { leagueId } = useParams();
  const { submitted } = props;

  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState(null);
  const [events, setEvents] = useState(null);
  const [months, setMonths] = useState(null);

  const handleScroll = () => {
    if (window.pageYOffset > 200) {
      if (!showScrollToTop) setShowScrollToTop(true);
    } else {
      if (showScrollToTop) setShowScrollToTop(false);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  useEffect(() => {
    const go = async () => {
      try {
        var apiLeague = await getData(
          `/leagues/${leagueId || props.leagueId}`,
          {
            queryStringParameters: {
              "expand[]": ["gameSystem", "gameStore", "gameStoreUser"],
            },
          }
        );

        if (apiLeague.photoFileId) {
          apiLeague.photo = await getData(
            "API",
            `/files/${apiLeague.photoFileId}`,
            {
              queryStringParameters: {},
            }
          );
        }

        setLeague(apiLeague);

        var queryParams = {
          limit: 100,
          sortKey: "eventDate",
          leagueId: leagueId || props.leagueId,
        };
        if (submitted !== undefined) {
          queryParams.submitted = submitted;
        }

        setEvents(null);
        await handlePagingAll("/events", queryParams, setEvents);
      } catch (err) {
        if (err.name === "HidePlacings Error") {
          postNotification("warning", err.message);
        } else {
          postNotification("error", err);
        }
        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!events) {
      setMonths(null);
      return;
    }

    var currentMoment = undefined;

    var newMonths = [];
    var currentMonth;
    for (let event of events) {
      if (!currentMoment) {
        currentMoment = event.timeZone
          ? moment(event.eventDate)
          : moment(event.eventDate).tz(event.timeZone);
        currentMonth = {
          label: currentMoment.format("MMMM YYYY"),
          date: currentMoment.format("YYYY-MM-DD"),
          events: [],
        };
      }
      var eventMoment = event.timeZone
        ? moment(event.eventDate)
        : moment(event.eventDate).tz(event.timeZone);

      while (currentMonth.label !== eventMoment.format("MMMM YYYY")) {
        if (currentMoment) newMonths.push(currentMonth);

        currentMoment = currentMoment.add(1, "month");
        currentMonth = {
          label: currentMoment.format("MMMM YYYY"),
          date: currentMoment.format("YYYY-MM-DD"),
          events: [],
        };
      }

      currentMonth.events.push(event);
    }

    if (currentMonth) {
      newMonths.push(currentMonth);
    }

    if (currentMoment) {
      var endDate = league.endDate
        ? moment(league.endDate)
        : league.startDate
        ? moment(league.startDate).endOf("month").add(11, "months")
        : moment(newMonths[0].date).endOf("month").add(11, "months");

      while (currentMonth.date < endDate.format("YYYY-MM-DD")) {
        currentMoment = currentMoment
          .endOf("month")
          .add(1, "day")
          .endOf("month");
        currentMonth = {
          label: currentMoment.format("MMMM YYYY"),
          date: currentMoment.format("YYYY-MM-DD"),
          events: [],
        };
        newMonths.push(currentMonth);
      }
    }

    setMonths(newMonths);

    if (loading) setLoading(false);
    // eslint-disable-next-line
  }, [events]);

  return (
    <Box sx={{ width: "100%" }}>
      {loading && <ModalLoadingFull />}
      {league && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Grid
            container
            sx={{
              gap: 2,
              p: 2,
            }}
          >
            <Grid item xs={12}>
              <Typography variant="h3" gutterBottom>
                {league?.name} Event Calendar
              </Typography>
            </Grid>

            {months?.length > 0 && (
              <Grid item xs={12}>
                {months.map((month, index) => (
                  <Accordion key={index} disabled={!month?.events?.length}>
                    <AccordionSummary
                      expandIcon={<BCPAngleDown />}
                      area-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                      <Typography>{month.label}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {month?.events?.length > 0 &&
                        month.events.map((event, eventIndex) => (
                          <ExternalLink
                            key={eventIndex}
                            href={`/event/${event.id}`}
                            target="_blank"
                            underline="none"
                          >
                            <Grid container sx={{ mb: 1 }}>
                              <Grid item xs={8}>
                                {event.name}
                              </Grid>
                              <Grid item xs={4} textAlign="right">
                                {(event.timeZone
                                  ? moment(event.eventDate)
                                  : moment(event.eventDate).tz(event.timeZone)
                                ).format("MMM Do, YYYY")}
                              </Grid>
                            </Grid>
                          </ExternalLink>
                        ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            )}
          </Grid>

          {showScrollToTop && (
            <Button
              variant="contained"
              color="info"
              size="large"
              onClick={() => {
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              }}
              sx={{
                position: "fixed",
                bottom: "40px",
                right: "10px",
                minWidth: "unset",
                borderRadius: "50%",
                padding: "unset",
              }}
            >
              <BCPCircleArrowUp sx={{ fontSize: "2.5rem", width: "2.5rem" }} />
            </Button>
          )}
        </Container>
      )}
    </Box>
  );
}
